import { findOption, typedKeys } from '@/shared/utils/typesUtils'
import { useTranslations } from 'next-intl'
import toasters from '../../../locales/fr/toasters.json'
import { isErrorWithCode } from '@/shared/utils/queryUtils'
import type { ToastData } from '@/shared/v2/shared/components/Toaster/ToasterProvider'
// eslint-disable-next-line data-layer/import-direction -- legacy: makalu still fires toasts from hooks (ERR-4), and this is the one bridge to the toaster
import { useToast } from '@/shared/v2/shared/components/Toaster/ToasterProvider'

export type ToastersKeys = keyof (typeof toasters)['toasters']

export const stringToToasterKeyValidator = (key: string): ToastersKeys | undefined => {
    return findOption(typedKeys(toasters['toasters']), key)
}

const toastTypeToIntent = {
    brand: 'info',
    positive: 'success',
    negative: 'error',
    warning: 'warning'
} as const satisfies Record<string, ToastData['intent']>

export const deprecated_useSetToast = (): ((
    key: ToastersKeys,
    type: keyof typeof toastTypeToIntent,
    errorMessage?: string
) => void) => {
    const t = useTranslations('toasters')
    const { showToast } = useToast()

    return (key: ToastersKeys, type: keyof typeof toastTypeToIntent, errorMessage?: string) => {
        return showToast({
            title: t(`${key}.${type}.title`),
            ...(errorMessage
                ? { description: t(`${key}.${type}.details`, { message: errorMessage }) }
                : { description: t(`${key}.${type}.description`) }),
            intent: toastTypeToIntent[type]
        })
    }
}

const hasToasterTranslation = (code: string): boolean => {
    return Object.keys(toasters['toasters']).includes(code)
}

export const useSetToast = () => {
    const { showToast } = useToast()
    const toasterTranslations = useTranslations('toasters')

    return (props: ToastData, error?: unknown) => {
        if (isErrorWithCode(error) && hasToasterTranslation(error.code)) {
            showToast({
                title: toasterTranslations(`${error.code}.negative.title`),
                description: toasterTranslations(`${error.code}.negative.description`),
                intent: 'error'
            })
            return
        }

        showToast(props)
    }
}
